import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";

import { UploadTenderDocumentModal } from "components/bids/bid_documents/UploadTenderDocumentModal";
import { createBidSchema } from "components/bids/utils";
import { useDocumentUploadProgress } from "components/content_library/DocumentUploadProgressProvider";
import { CreditAccessWrapper } from "components/paywall/CreditAccessWrapper";
import Button from "components/ui/button/Button";
import toast from "components/ui/toast/Toast";
import { useBid } from "lib/hooks/api/bid/bids/useBid";
import { useCreateBid } from "lib/hooks/api/bid/bids/useCreateBid";
import { useDefaultBidWorkflowStage } from "lib/hooks/api/bid/bids/useDefaultBidWorkflowStage";
import {
  DEFAULT_CUSTOMER_DOCUMENTS_PAGINATION,
  useCustomerDocuments,
} from "lib/hooks/api/customer_documents/useCustomerDocuments";
import { useTriggerBidDocumentUpload } from "lib/hooks/api/customer_documents/useTriggerBidDocumentUpload";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import {
  useBidOrchestratorStream,
  useBidOrchestratorStreamContext,
} from "lib/providers/bid_orchestrator/BidOrchestratorStreamProvider";
import { useRecordViewer } from "lib/providers/RecordViewer";
import { routeSegments } from "lib/routes";
import { EventData, EventNames, useTracking } from "lib/tracking";
import {
  BidReportType,
  CustomerDocumentSortBy,
  CustomerDocumentSortOrder,
  CustomerDocumentTypeFilter,
} from "lib/types/graphQLEnums";
import { CreateBidFormOutput } from "../bids/create_bid/CreateBidForm";

type Props = {
  trackingData: EventData;
  defaultValues: Partial<CreateBidFormOutput>;
  sourceUrl: string | undefined;
  fullWidth?: boolean;
  existingBidId?: string;
};

export function CreateBidNoBidReportButton({
  trackingData,
  defaultValues,
  sourceUrl,
  fullWidth,
  existingBidId,
}: Props) {
  const [createdBidId, setCreatedBidId] = useState<string | undefined>(undefined);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReportInProgress, setIsReportInProgress] = useState(false);

  const navigate = useNavigate();
  // Closes the NoticePreview drawer when we navigate away — otherwise it stays
  // open over the destination page. No-op when the component isn't rendered
  // inside the drawer.
  const { closeRecord } = useRecordViewer();
  const { logEvent } = useTracking();
  const { data: currentUser } = useCurrentUser();

  const { allUploaded, uploadStatuses } = useDocumentUploadProgress(existingBidId ?? createdBidId);
  const triggerUpload = useTriggerBidDocumentUpload(existingBidId, { skipProcessing: true });

  const bidIdRef = useRef<string | undefined>(existingBidId);

  // Keep bidIdRef in sync when createdBidId is set after bid creation.
  useEffect(() => {
    if (createdBidId) bidIdRef.current = createdBidId;
  }, [createdBidId]);

  const { data: existingBidData } = useBid(
    { id: existingBidId ?? "" },
    { enabled: !!existingBidId },
  );
  const existingBidReportId = existingBidData?.bidReport?.id;

  const resourceId = existingBidId ?? createdBidId;

  // The bid's already-attached docs, fetched once here so they can be captured
  // into the queued orchestrator (the driver no longer refetches per-bid). A
  // brand-new bid simply has none yet.
  const { data: existingDocuments } = useCustomerDocuments(
    {
      resourceId: resourceId ?? "",
      sortBy: CustomerDocumentSortBy.FileName,
      sortOrder: CustomerDocumentSortOrder.Asc,
      documentType: CustomerDocumentTypeFilter.BidDocument,
    },
    DEFAULT_CUSTOMER_DOCUMENTS_PAGINATION,
    { enabled: !!resourceId },
  );

  const { entry: enrichmentEntry } = useBidOrchestratorStream(resourceId);
  const { queueBidOrchestrator } = useBidOrchestratorStreamContext();

  const bidReportId = enrichmentEntry?.requirementSummaryReportId ?? existingBidReportId;

  const qualificationUrl =
    resourceId && bidReportId
      ? `/bids/${resourceId}/${routeSegments.requirementSummary}/${bidReportId}`
      : undefined;

  const { defaultStageId } = useDefaultBidWorkflowStage(defaultValues);

  const { mutate: createBid } = useCreateBid(trackingData);

  const prevAllUploadedRef = useRef(allUploaded);
  useEffect(() => {
    const wasNotAllUploaded = !prevAllUploadedRef.current;
    prevAllUploadedRef.current = allUploaded;

    if (!isReportInProgress) return;
    const justFinishedUploading = wasNotAllUploaded && allUploaded;
    if (!justFinishedUploading) return;
    // Vacuous `allUploaded` true (no files yet) — wait for the real upload.
    if (uploadStatuses.length === 0) return;

    const bidId = bidIdRef.current;
    if (!bidId) return;

    queueBidOrchestrator(bidId, {
      reportType: BidReportType.RequirementSummary,
      expectsUpload: false,
      extractBidDetails: false,
      existingDocumentIds: existingDocuments?.map((doc) => doc.id) ?? [],
    });
    setIsReportInProgress(false);
    closeRecord();
    void navigate({ to: `/bids/${bidId}/${routeSegments.requirementSummary}` });
  }, [
    allUploaded,
    isReportInProgress,
    uploadStatuses,
    existingDocuments,
    queueBidOrchestrator,
    closeRecord,
    navigate,
  ]);

  // Called when the user clicks "Create report" inside the modal.
  // Modal closes immediately; this function runs the bid creation + upload in background.
  const handleModalSubmit = useCallback(
    (files: File[]) => {
      setIsReportInProgress(true);

      const trackReportCreated = (bidId: string) =>
        logEvent(EventNames.qualificationReportCreated, {
          "Action type": "Button clicked",
          "Number of documents": files.length,
          "Bid id": bidId,
          "Report type": "Requirement summary",
        });

      if (existingBidId) {
        bidIdRef.current = existingBidId;
        trackReportCreated(existingBidId);
        triggerUpload(files, existingBidId);
        return;
      }

      if (!defaultStageId) {
        toast.error("Could not determine default stage for bid");
        setIsReportInProgress(false);
        return;
      }

      const { entities, ...bid } = createBidSchema.parse({
        ...defaultValues,
        stageId: defaultValues.stageId ?? defaultStageId,
        assignedToId: defaultValues.assignedToId ?? currentUser?.guid,
      });

      createBid(
        { bid, entities: { entities: entities ?? [] } },
        {
          onSuccess: (data) => {
            toast.success(`Created bid - ${data.createBid.name}`);
            const newBidId = data.createBid.id;
            bidIdRef.current = newBidId;
            setCreatedBidId(newBidId);
            trackReportCreated(newBidId);
            triggerUpload(files, newBidId);
          },
          onError: () => {
            toast.error("Failed to create bid");
            setIsReportInProgress(false);
          },
        },
      );
    },
    [createBid, defaultStageId, defaultValues, existingBidId, triggerUpload, logEvent, currentUser],
  );

  const showViewButton = !!qualificationUrl || !!existingBidReportId || isReportInProgress;

  return (
    <>
      {showViewButton ? (
        <Button
          buttonType="secondary"
          loading={isReportInProgress}
          onClick={() => {
            logEvent(EventNames.qualificationReportActioned, {
              ...trackingData,
              "Report status": "New",
            });
            if (qualificationUrl) {
              closeRecord();
              void navigate({ to: qualificationUrl });
            }
          }}
          fullWidth={fullWidth}
        >
          View requirement summary
        </Button>
      ) : (
        <CreditAccessWrapper
          requiredDataType="BIDS"
          creditType="BID"
          paywallFeatureType="BID_NO_BID_REPORTS"
          strategyPaywall={false}
        >
          <Button
            buttonType="primary"
            onClick={(e) => {
              e.stopPropagation();
              logEvent(EventNames.qualificationStarted, trackingData);
              setIsModalOpen(true);
            }}
            fullWidth={fullWidth}
            loading={isReportInProgress}
          >
            Create requirement summary
          </Button>
        </CreditAccessWrapper>
      )}

      <UploadTenderDocumentModal
        bidId={existingBidId ?? createdBidId}
        isOpen={isModalOpen}
        onOpenChange={setIsModalOpen}
        sourceUrl={sourceUrl}
        onSubmit={handleModalSubmit}
        primaryButtonText="Create report"
      />
    </>
  );
}
